import { Box, Stack, ThemeProvider, Typography } from '@mui/material';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import PasswordStrengthIndicator from 'components/common/PasswordStrengthIndicator/PasswordStrengthIndicator';
import AccountTabs from 'components/v2/accountTabs';
import PageHeader from 'components/v2/common/pageHeader';
import PageBackground from 'components/v2/PageBackground';
import { ProfileAvatar } from 'views/v2/auth/components/profileAvatar';

import { profileLanguages } from 'constant';
import {
    useMyAccount,
    useProfilePicture,
    useUpdateMyAccount,
    useUpdateProfilePicture,
} from 'hooks/useMyAccount';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import appThemeV2 from 'theme/themeV2';
import type { ChangePasswordInterface, MyAccountInterface } from 'types';
import { generateOptionsFromString } from 'utils/dataFormatter';
import { getErrorMessage } from 'utils/v2/errorHelper';
import {
    changePasswordDefaultValues,
    changePasswordValidationSchema,
    myAccountDefaultValues,
    myAccountValidationSchema,
} from 'utils/validations/myAccount';

const profileLanguageOptions = generateOptionsFromString(Object.values(profileLanguages));

const CHANGE_PASSWORD_HASH = '#change-password';

const MyAccount = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { translate } = useTranslate();
    const { data: myAccount } = useMyAccount();
    const { data: profilePictureUrl } = useProfilePicture();

    const updateMyAccount = useUpdateMyAccount();
    const updatePassword = useUpdateMyAccount();
    const updateProfilePicture = useUpdateProfilePicture();

    const showChangePassword = location.hash === CHANGE_PASSWORD_HASH;
    const initialValues = { ...myAccountDefaultValues, ...myAccount };

    const closeChangePassword = () => navigate({ hash: '' });

    const handleSubmit = async (
        values: Partial<MyAccountInterface>,
        formikHelpers: FormikHelpers<Partial<MyAccountInterface>>
    ) => {
        const { resetForm } = formikHelpers;
        try {
            await updateMyAccount.mutateAsync({
                ...values,
                firstName: values.firstname?.trim(),
                lastName: values.lastname?.trim(),
            });

            resetForm();
            dispatch(setAlert({ message: 'Profile updated successfully', type: 'success' }));
        } catch (error) {
            const message = getErrorMessage(error, 'Failed to change password');
            dispatch(setAlert({ message, type: 'error' }));
        }
    };

    const handlePasswordSubmit = async (
        values: ChangePasswordInterface,
        formikHelpers: FormikHelpers<ChangePasswordInterface>
    ) => {
        try {
            await updatePassword.mutateAsync(values);
            formikHelpers.resetForm();
            dispatch(
                setAlert({ message: translate('passwordUpdatedSuccessfully'), type: 'success' })
            );
            closeChangePassword();
        } catch (error) {
            const message = getErrorMessage(error, translate('passwordUpdateFailed'));
            dispatch(setAlert({ message, type: 'error' }));
        }
    };

    const handleFileChange = (file?: File) => {
        if (!file) return;
        updateProfilePicture.mutate(file, {
            onSuccess: () => {
                dispatch(setAlert({ message: 'Profile picture updated', type: 'success' }));
            },
            onError: (error) => {
                const message = error instanceof Error ? error.message : 'Upload failed';
                dispatch(setAlert({ message, type: 'error' }));
            },
        });
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground maxWidth="sm">
                <AccountTabs />

                <Box
                    sx={{
                        mb: 4,
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                    }}
                >
                    <ProfileAvatar
                        src={profilePictureUrl || undefined}
                        alt={`${initialValues.firstname || ''} ${initialValues.lastname || ''}`}
                        uploading={updateProfilePicture.isPending}
                        onFileSelect={handleFileChange}
                    />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {initialValues.firstname} {initialValues.lastname}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            ID: {initialValues.file_number || '—'}
                        </Typography>
                    </Box>
                </Box>

                {showChangePassword ? (
                    <>
                        <PageHeader size="small" title="changePassword" />

                        <Formik
                            initialValues={changePasswordDefaultValues}
                            validationSchema={changePasswordValidationSchema}
                            onSubmit={handlePasswordSubmit}
                        >
                            {({ values }) => (
                                <Form>
                                    <Stack spacing={1}>
                                        <FormField
                                            name="currentPassword"
                                            label="currentPassword"
                                            type="password"
                                            disabled={updatePassword.isPending}
                                        />
                                        <FormField
                                            name="newPassword"
                                            label="newPassword"
                                            type="password"
                                            disabled={updatePassword.isPending}
                                        />
                                        <PasswordStrengthIndicator
                                            password={values.newPassword ?? ''}
                                        />
                                        <FormField
                                            name="repeatPassword"
                                            label="repeatPassword"
                                            type="password"
                                            disabled={updatePassword.isPending}
                                        />
                                        <SubmitButton
                                            loading={updatePassword.isPending}
                                            sx={{ alignSelf: 'flex-end' }}
                                        >
                                            changePassword
                                        </SubmitButton>
                                    </Stack>
                                </Form>
                            )}
                        </Formik>
                    </>
                ) : (
                    <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        validationSchema={myAccountValidationSchema}
                        onSubmit={handleSubmit}
                    >
                        <Form>
                            <FormGridLayout>
                                <FormField
                                    name="firstname"
                                    label="First Name"
                                    disabled={updateMyAccount.isPending}
                                />
                                <FormField
                                    name="lastname"
                                    label="Last Name"
                                    disabled={updateMyAccount.isPending}
                                />
                                <FormField
                                    name="email"
                                    label="Email"
                                    type="email"
                                    disabled={updateMyAccount.isPending}
                                />
                                <FormField
                                    name="language_setting"
                                    label="Language"
                                    type="select"
                                    options={profileLanguageOptions}
                                    disabled={updateMyAccount.isPending}
                                />
                            </FormGridLayout>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <SubmitButton loading={updateMyAccount.isPending}>
                                    {translate('saveChanges')}
                                </SubmitButton>
                            </Box>
                        </Form>
                    </Formik>
                )}
            </PageBackground>
        </ThemeProvider>
    );
};

export default MyAccount;
